import {
  Box,
  Checkbox,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PerformanceList from "./PerformanceList";
import SubContainer from "./SubContainer";
import { format, isBefore, isEqual, parse, subYears } from "date-fns";
import { DATE_FORMATS } from "../../../Utils/CONST";
import StyledMenuButton from "./StyledMenuButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { getCurrencySymbol } from "../../../Utils/Utils";
import { useSelector } from "react-redux";

const CustomTooltip = ({
  active,
  payload,
  label,
  metric,
  currencySymbol,
  doComparison,
}) => {
  if (!active || !payload?.length) return null;

  const currentYear = new Date().getFullYear();
  const current = payload.find((p) => p.dataKey === "futureValue");
  const comparison = payload.find((p) => p.dataKey === "comparisonFuture");

  const isCurrencyMetric = ["Revenue", "ADR", "RevPAR"].includes(metric);

  const formatValue = (value) => {
    if (value === null) return "—";
    if (isCurrencyMetric) {
      return `${currencySymbol}${value.toLocaleString()}`;
    }
    return metric === "Occupancy" ? `${value}%` : value;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#1F2937",
        border: "1px solid #4B5563",
        borderRadius: "8px",
        padding: "0.75rem 1rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        color: "#E5E7EB",
      }}
    >
      {/* Tooltip header (date / label) */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          marginBottom: "0.5rem",
          color: "#F3F4F6",
        }}
      >
        {label}
      </Typography>

      <Stack spacing={0.5}>
        {/* Comparison Metric */}
        {comparison && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: comparison.color || "#94A3B8",
                }}
              />
              <Typography variant="body2" sx={{ color: "#D1D5DB" }}>
                {currentYear - 1}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
              {formatValue(comparison.value)}
            </Typography>
          </Stack>
        )}

        {/* Current Metric */}
        {current && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: current.color || "#4ADE80",
                }}
              />
              <Typography variant="body2" sx={{ color: "#D1D5DB" }}>
                {doComparison ? currentYear : metric}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
              {formatValue(current.value)}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const PropertyPerformanceMetrics = ({
  topPerforming,
  lowPerforming,
  chartData = [],
  dataKey,
  isLoading,
  showPerformanceLists,
  doComparison,
  comparisonChartData,
}) => {
  const [metric, setMetric] = useState("Revenue");
  const [anchorEl, setAnchorEl] = useState(null);
  const hotels = useSelector((state) => state.auth.hotels);
  const hotelCurrency = hotels[0]?.hotelCurrency || "INR";
  const currencySymbol = getCurrencySymbol(hotelCurrency);

  const formattedChartData = (chartData, date) => {
    const currDate = date ? date : new Date();
    let currDataArray = [];
    for (const chartItem of chartData || []) {
      const chartItemDate = parse(
        chartItem.date,
        DATE_FORMATS.DATE_2,
        new Date()
      );
      const isItemBeforeOrEqualNow =
        isBefore(chartItemDate, currDate) || isEqual(chartItemDate, currDate);
      const formattedDate = format(
        parse(chartItem.date, DATE_FORMATS.DATE_2, new Date()),
        DATE_FORMATS.DATE_3
      );
      let newItem = { date: formattedDate };
      switch (metric.toLowerCase()) {
        case "revenue":
          newItem = {
            ...newItem,
            actualValue: isItemBeforeOrEqualNow ? chartItem.revenue : null,
            futureValue: chartItem.revenue,
          };
          break;
        case "adr":
          newItem = {
            ...newItem,
            actualValue: isItemBeforeOrEqualNow ? chartItem.adr : null,
            futureValue: chartItem.adr,
          };
          break;
        case "revpar":
          newItem = {
            ...newItem,
            actualValue: isItemBeforeOrEqualNow ? chartItem.revPAR : null,
            futureValue: chartItem.revPAR,
          };
          break;
        case "occupancy":
          newItem = {
            ...newItem,
            actualValue: isItemBeforeOrEqualNow ? chartItem.occupancy : null,
            futureValue: chartItem.occupancy,
          };
          break;
        // case "pickup":
        //   newItem = {
        //     ...newItem,
        //     actualValue: isItemBeforeOrEqualNow ? chartItem.roomsSold : null,
        //     futureValue: chartItem.roomsSold,
        //   };
        //   break;
        default:
          break;
      }

      currDataArray.push(newItem);
    }
    return currDataArray;
  };

  if (isLoading) {
    return (
      <Box height={"320px"}>
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={320}
          animation="wave"
          sx={{
            borderRadius: "8px",
            "&.MuiSkeleton-root": {
              backgroundColor: "#b6bed350",
            },
          }}
        />
      </Box>
    );
  }

  const mergedData = formattedChartData(chartData).map((currItem) => {
    const lastYearItem = formattedChartData(
      comparisonChartData,
      subYears(new Date(), 1)
    ).find((comp) => comp.date === currItem.date);

    return {
      date: currItem.date,
      actualValue: currItem.actualValue,
      futureValue: currItem.futureValue,
      comparisonActual: lastYearItem?.actualValue || null,
      comparisonFuture: lastYearItem?.futureValue || null,
    };
  });

  const yAxisMarginLeft = (() => {
    return (
      Math.max(
        ...mergedData.map((item) => item.futureValue?.toString().length)
      ) + 16
    );
  })();

  return (
    <Grid container columnSpacing={2} width={"100%"}>
      {showPerformanceLists && (
        <Grid container item md={3} rowGap={3} style={{ paddingLeft: 0 }}>
          <>
            <Grid item xs={12}>
              <PerformanceList
                title="Top Performing"
                items={topPerforming}
                icon="↑"
                iconColor={"green"}
              />
            </Grid>
            <Grid item xs={12}>
              <PerformanceList
                title="Low Performing"
                items={lowPerforming}
                icon="↓"
                iconColor={"red"}
              />
            </Grid>
          </>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        md={showPerformanceLists ? 9 : 12}
        style={{ ...(!showPerformanceLists && { paddingLeft: 0 }) }}
      >
        <SubContainer
          sx={{ height: "100%" }}
          spacing={2}
          minHeight={showPerformanceLists ? undefined : "480px"}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant={"h5"} fontWeight={700} color={"white"}>
              Monthly Performance
            </Typography>
            <StyledMenuButton
              variant="contained"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              endIcon={
                !anchorEl ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />
              }
            >
              <span
                style={{
                  display: "block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  flex: 1,
                  textAlign: "left",
                }}
              >
                {metric}
              </span>
            </StyledMenuButton>
          </Stack>
          <ResponsiveContainer width="100%" height={"100%"}>
            <ComposedChart
              data={mergedData}
              margin={{ top: 15, right: 32, left: yAxisMarginLeft, bottom: 10 }}
            >
              <XAxis
                dataKey="date"
                tick={{ fill: "#b6bed3" }}
                angle={-45}
                tickMargin={10}
              />
              <YAxis
                tickFormatter={(v) => {
                  const isCurrencyMetric = [
                    "Revenue",
                    "ADR",
                    "RevPAR",
                  ].includes(metric);
                  if (isCurrencyMetric) {
                    return `${currencySymbol}${v.toLocaleString()}`;
                  }
                  return metric === "Occupancy" ? `${v}%` : v;
                }}
                tick={{ fill: "#b6bed3" }}
              />
              <CartesianGrid strokeDasharray={"3 3"} strokeOpacity={0.5} />
              <Tooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    metric={metric}
                    currencySymbol={currencySymbol}
                    doComparison={doComparison}
                  />
                )}
              />

              <Area
                type="monotone"
                dataKey={"actualValue"}
                stroke="#fff"
                strokeWidth={3.5}
                fill="white"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="futureValue"
                stroke="#fff"
                strokeWidth={3}
                strokeDasharray="3 3"
                dot={false}
              />
              {doComparison && (
                <>
                  <Line
                    type="monotone"
                    dataKey={"comparisonFuture"}
                    stroke="#fff"
                    strokeWidth={2}
                    strokeDasharray="10 10"
                    fill="white"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="comparisonFuture"
                    stroke="#fff"
                    strokeWidth={2}
                    strokeDasharray="10 10"
                    dot={false}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </SubContainer>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {[
          "Revenue",
          "ADR",
          "RevPAR",
          "Occupancy",
          // "Pickup"
        ].map((m) => (
          <MenuItem key={m} onClick={() => setMetric(m)} dense>
            <Checkbox checked={metric === m} />
            <ListItemText primary={m} />
          </MenuItem>
        ))}
      </Menu>
    </Grid>
  );
};

export default PropertyPerformanceMetrics;
